import { useState, useEffect, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import Textarea from '@/components/ui/Textarea'
import Button from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

interface OutletContext {
  currentStep: number
  goToStep: (index: number) => void
  handleForward: () => void
  handleBack: () => void
}

interface Answers {
  genre: string
  lastBook: string
  hopes: string
}

const questions = [
  {
    key: 'genre' as const,
    labelKey: 'onboarding.reader_q1',
    placeholderKey: 'onboarding.reader_q1_ph',
  },
  {
    key: 'lastBook' as const,
    labelKey: 'onboarding.reader_q2',
    placeholderKey: 'onboarding.reader_q2_ph',
  },
  {
    key: 'hopes' as const,
    labelKey: 'onboarding.reader_q3',
    placeholderKey: 'onboarding.reader_q3_ph',
  },
]

export default function ReaderScreen() {
  const { t } = useTranslation()
  const { handleForward } = useOutletContext<OutletContext>()
  const { user } = useAuth()
  const [answers, setAnswers] = useState<Answers>({
    genre: '',
    lastBook: '',
    hopes: '',
  })
  const [visible, setVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  const handleChange = useCallback((key: keyof Answers, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!user) return
    // Nothing answered → treat "next" like skip: move on without writing
    // an empty stub into the profile.
    if (!answers.genre.trim() && !answers.lastBook.trim() && !answers.hopes.trim()) {
      handleForward()
      return
    }
    setSaving(true)
    setError(null)

    try {
      // Store answers as a combined bio/reader_portrait string
      // This is stubbed — in the full implementation, this feeds an AI portrait generator
      const combinedText = [
        `${t('onboarding.bio_prefix_genres')}${answers.genre}`,
        `${t('onboarding.bio_prefix_moved')}${answers.lastBook}`,
        `${t('onboarding.bio_prefix_expectations')}${answers.hopes}`,
      ].join('\n\n')

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          // DB constraint: bio text CHECK (length(bio) <= 320) — defensive cap
          bio: combinedText.slice(0, 320),
          reader_portrait: answers.genre, // Stub: stores primary genre interest
        })
        .eq('id', user.id)

      if (updateError) throw updateError
      handleForward()
    } catch (err: unknown) {
      console.error('Onboarding reader save failed:', err)
      const message = err instanceof Error ? err.message
        : (err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : t('onboarding.save_failed'))
      setError(message)
    } finally {
      setSaving(false)
    }
  }, [user, answers, handleForward])

  const handleSkip = useCallback(() => {
    // Skip the reader questions entirely — no bio update.
    handleForward()
  }, [handleForward])

  return (
    <div className="flex flex-col py-8">
      {/* Title */}
      <h1
        className={`font-display text-3xl text-ink mb-2 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.reader_title')}
      </h1>
      <p
        className={`font-body text-ink-muted leading-relaxed mb-8 transition-all duration-700 delay-100 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.reader_subtitle')}
      </p>

      {/* Question cards */}
      <div className="space-y-6 mb-8">
        {questions.map((q, i) => (
          <div
            key={q.key}
            className={`card-warm p-5 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: `${200 + i * 150}ms` }}
          >
            <label
              htmlFor={q.key}
              className="block font-display text-lg text-ink mb-3"
            >
              {t(q.labelKey)}
            </label>
            {/* maxLength 88 per field: prefixes + two \n\n ≈ 54 chars,
                3×88 = 264 → combined stays ≤ 320 (bio CHECK constraint) */}
            <Textarea
              id={q.key}
              placeholder={t(q.placeholderKey)}
              value={answers[q.key]}
              onChange={(e) => handleChange(q.key, e.target.value)}
              maxLength={88}
              className="w-full min-h-[100px] font-body text-base resize-none bg-transparent border-ink/10 focus:border-green focus:ring-green/20"
            />
            <p className="mb-2 text-right font-body text-xs text-ink-muted">
              {answers[q.key].length} / 88
            </p>
          </div>
        ))}
      </div>

      {error && (
        <p className="mb-4 text-terra text-sm font-body">{error}</p>
      )}

      {/* Submit */}
      <div
        className={`transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ transitionDelay: `${200 + questions.length * 150}ms` }}
      >
        <Button
          onClick={handleSubmit}
          disabled={saving}
          className="w-full h-12 btn-primary"
        >
          {saving ? t('common.saving') : t('onboarding.next')}
        </Button>
        {/* Skip — the questions are optional */}
        <div className="pt-4 text-center">
          <button
            onClick={handleSkip}
            disabled={saving}
            className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink transition-colors"
          >
            {t('onboarding.skip')}
          </button>
        </div>
      </div>
    </div>
  )
}
